import { collection, doc, serverTimestamp, writeBatch } from "firebase/firestore";
import { db } from "./firebase";
import {
  buildDirectorySubmissionPayload,
  DIRECTORY_SERVICE_TYPES,
  validateDirectorySubmission,
} from "./DirectorySubmissionForm";
import { normalizeExternalUrl } from "./urlUtils";

export const MAP_SERVICE_TYPES = DIRECTORY_SERVICE_TYPES;

export const EMPTY_MAP_SUBMISSION = {
  name: "",
  serviceType: "Community",
  description: "",
  website: "",
  contactEmail: "",
  contactPhone: "",
  address: "",
  postcode: "",
  includeDirectoryListing: false,
  confirmedAccurate: false,
};

const clean = (value) => String(value || "").trim();

export function mapSubmissionToDirectoryValues(values) {
  return {
    name: clean(values.name),
    type: values.serviceType,
    description: clean(values.description),
    website: clean(values.website),
    contactEmail: clean(values.contactEmail),
    contactPhone: clean(values.contactPhone),
    address: clean(values.address),
    postcode: clean(values.postcode),
    confirmedAccurate: values.confirmedAccurate === true,
  };
}

export function validateMapSubmission(values, pin) {
  const errors = {};
  const name = clean(values.name);
  const description = clean(values.description);
  const website = clean(values.website);
  const email = clean(values.contactEmail);
  const phone = clean(values.contactPhone);
  const address = clean(values.address);
  const postcode = clean(values.postcode);

  if (name.length < 2 || name.length > 120) {
    errors.name = "Enter a location name between 2 and 120 characters.";
  }
  if (!MAP_SERVICE_TYPES.includes(values.serviceType)) {
    errors.serviceType = "Choose a valid category.";
  }
  if (description.length > 1000) {
    errors.description = "Keep the description to 1,000 characters or fewer.";
  }
  if (website.length > 300 || (website && !normalizeExternalUrl(website))) {
    errors.website = "Enter a valid website address.";
  }
  if (email.length > 254 || (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) {
    errors.contactEmail = "Enter a valid email address.";
  }
  if (phone.length > 40) errors.contactPhone = "Keep the phone number to 40 characters or fewer.";
  if (address.length > 300) errors.address = "Keep the address to 300 characters or fewer.";
  if (postcode.length > 12) errors.postcode = "Keep the postcode to 12 characters or fewer.";
  if (
    !pin
    || typeof pin.lat !== "number"
    || typeof pin.lng !== "number"
    || pin.lat < -90
    || pin.lat > 90
    || pin.lng < -180
    || pin.lng > 180
  ) {
    errors.pin = "Click the map to place the location pin.";
  }

  if (values.includeDirectoryListing) {
    Object.assign(
      errors,
      validateDirectorySubmission(mapSubmissionToDirectoryValues(values))
    );
  }

  return errors;
}

export function buildMapLocationPayload(values, pin, createdAt) {
  const contactEmail = clean(values.contactEmail);
  const contactPhone = clean(values.contactPhone);
  const address = clean(values.address);

  return {
    name: clean(values.name),
    serviceType: values.serviceType,
    description: clean(values.description),
    keywords: [values.serviceType, clean(values.postcode).toUpperCase()]
      .filter(Boolean)
      .join(", "),
    website: normalizeExternalUrl(values.website),
    contact: contactEmail || contactPhone || address,
    contactEmail,
    contactPhone,
    address,
    postcode: clean(values.postcode).toUpperCase(),
    position: { lat: pin.lat, lng: pin.lng },
    status: "pending",
    createdAt,
    type: "location",
  };
}

export async function submitMapLocation(values, pin) {
  const errors = validateMapSubmission(values, pin);
  if (Object.keys(errors).length > 0) {
    const error = new Error("MAP_SUBMISSION_INVALID");
    error.validationErrors = errors;
    throw error;
  }

  const batch = writeBatch(db);
  const createdAt = serverTimestamp();
  const mapLocationRef = doc(collection(db, "mapLocations"));
  batch.set(mapLocationRef, buildMapLocationPayload(values, pin, createdAt));

  let directoryListingRef = null;
  if (values.includeDirectoryListing) {
    directoryListingRef = doc(collection(db, "directorySubmissions"));
    batch.set(
      directoryListingRef,
      buildDirectorySubmissionPayload(
        mapSubmissionToDirectoryValues(values),
        createdAt
      )
    );
  }

  await batch.commit();
  return {
    mapLocationId: mapLocationRef.id,
    directoryListingId: directoryListingRef?.id || null,
  };
}
